import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { requestYieldCallback } from '@theme/utilities';

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [Refs = {}]
 *
 * @extends Component<Refs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('change', this.variantChanged.bind(this));
    
    // CRITICAL: Override the theme's built-in URL update to prevent conflicts
    this._overrideThemeUrlUpdate();
    
    // Initialize URL with current variant if one is selected
    this._initializeUrl();
  }

  /**
   * Initialize the URL with the currently selected variant on page load
   */
  _initializeUrl() {
    const selectedOption = this.selectedOption;
    if (!selectedOption) return;

    const variantId = selectedOption.dataset.variantId;
    if (!variantId) return;

    const url = new URL(window.location.href);
    
    // Only update if variant param is missing or different
    if (url.searchParams.get('variant') !== variantId) {
      url.searchParams.set('variant', variantId);
      window.history.replaceState({ variantId }, '', url.toString());
    }
    
    // Store target URL for restoration checks
    this._targetUrl = url.toString();
    this._targetVariantId = variantId;
  }

  /**
   * Override theme's built-in URL update methods to prevent conflicts
   */
  _overrideThemeUrlUpdate() {
    // Store original methods
    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;
    
    // Flag to track if we're in the middle of our own update
    this._isUpdatingUrl = false;
    
    // Override pushState to prevent theme from changing URL during our operations
    window.history.pushState = function(...args) {
      // If we're in the middle of our variant update, block theme's URL changes
      if (this._isUpdatingUrl) {
        console.log('Blocked theme pushState during variant update');
        return;
      }
      return originalPushState.apply(this, args);
    }.bind(this);
    
    // Override replaceState similarly
    window.history.replaceState = function(...args) {
      if (this._isUpdatingUrl) {
        console.log('Blocked theme replaceState during variant update');
        return;
      }
      return originalReplaceState.apply(this, args);
    }.bind(this);
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);
    this.dispatchEvent(new VariantSelectedEvent({ id: selectedOption.dataset.optionValueId ?? '' }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    const currentUrlPath = this.dataset.productUrl?.split('?')[0];
    const newUrlPath = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrlPath && newUrlPath !== currentUrlPath;

    // Set flag before URL update to block theme interference
    this._isUpdatingUrl = true;
    
    try {
      if (isOnProductPage) {
        this.updateBrowserUrl(selectedOption, loadsNewProduct, newUrlPath);
      }
      
      const requestUrl = this.buildRequestUrl(selectedOption);
      this.fetchUpdatedSection(requestUrl, loadsNewProduct);
    } finally {
      // Ensure flag is reset even if error occurs
      setTimeout(() => {
        this._isUpdatingUrl = false;
      }, 100);
    }
  }

  /**
   * Helper to update the URL and ensure it persists.
   */
  updateBrowserUrl(selectedOption, loadsNewProduct, newUrlPath) {
    const url = new URL(window.location.href);
    const variantId = selectedOption.dataset.variantId;

    if (variantId) {
      url.searchParams.set('variant', variantId);
    }

    if (loadsNewProduct && newUrlPath) {
      url.pathname = newUrlPath;
    }

    if (url.href !== window.location.href) {
      const method = loadsNewProduct ? 'pushState' : 'replaceState';
      window.history[method]({ variantId }, '', url.toString());
    }
    
    // Store the URL we want to maintain
    this._targetUrl = url.toString();
    this._targetVariantId = variantId;
  }

  /**
   * Fetches the updated section.
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        if (shouldMorphMain) {
          this.updateMain(html);
        } else {
          const newProduct = this.updateVariantPicker(html);
          if (this.selectedOptionId) {
            this.dispatchEvent(
              new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
              })
            );
          }
        }

        // CRITICAL: Restore URL immediately and after a delay to catch any async resets
        this._restoreUrl();
        setTimeout(() => this._restoreUrl(), 0);
        setTimeout(() => this._restoreUrl(), 50);
        setTimeout(() => this._restoreUrl(), 100);
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * Restore the URL to the target variant URL
   */
  _restoreUrl() {
    if (this._targetUrl && window.location.href !== this._targetUrl) {
      window.history.replaceState(
        { variantId: this._targetVariantId }, 
        '', 
        this._targetUrl
      );
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);
      if (!targetElement) throw new Error('Target element not found');
      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);
      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }
      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    if (this.closest('quick-add-component') || this.closest('swatches-variant-picker-component')) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=section-rendering-product-card&${params.join('&')}`;
    }
    return `${productUrl}?${params.join('&')}`;
  }

  updateVariantPicker(newHtml) {
    let newProduct;
    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());
    if (!newVariantPickerSource) throw new Error('No source found');

    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;
      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }
      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource);
    return newProduct;
  }

  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');
    if (!main || !newMain) throw new Error('No main source found');
    morph(main, newMain);
  }

  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');
    return selectedOption instanceof HTMLElement ? selectedOption : undefined;
  }

  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;
    if (!optionValueId) throw new Error('No option value ID found');
    return optionValueId;
  }

  get selectedOptionsValues() {
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));
    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;
      if (!optionValueId) throw new Error('No option value ID found');
      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}